// ===== AquaOS Persistence Service =====
// Saves window layout + desktop state to localStorage so sessions survive reloads.
// App-specific document content goes to IndexedDB (see idb.js).

const SESSION_KEY = 'aquaos_session_v4';
const SESSION_VERSION = 4;

/**
 * Serialize the current window list into a JSON-safe snapshot.
 * We drop `component` (not serialisable) and `props` (app-specific).
 */
export function serializeWindows(windows) {
  return windows.map((w) => ({
    id: w.id,
    appId: w.appId,
    title: w.title,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    minimized: w.minimized,
    spaceId: w.spaceId ?? 1,
    fullscreen: w.fullscreen ?? false,
    splitSide: w.splitSide ?? null,
    zoomed: w.zoomed ?? false,
    prevX: w.prevX,
    prevY: w.prevY,
    prevW: w.prevW,
    prevH: w.prevH,
    props: w.props ?? {},
    // app-specific serialisable state (set by apps via setWindowMeta)
    meta: w.meta ?? {},
  }));
}

/** Save the full session snapshot to localStorage. */
export function saveSession(windows, spacesState) {
  try {
    const payload = {
      v: SESSION_VERSION,
      ts: Date.now(),
      windows: serializeWindows(windows),
      activeSpaceId: spacesState?.activeSpaceId ?? 1,
      spaces: spacesState?.spaces ?? [{ id: 1, name: 'Desktop 1' }],
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  } catch (e) {
    console.warn('[persist] Failed to save session:', e);
  }
}

/** Load a session snapshot from localStorage. Returns null if none or corrupted. */
export function loadSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return validateSession(migrateSession(parsed));
  } catch (e) {
    console.warn('[persist] Corrupted session, starting fresh:', e);
    return null;
  }
}

export function migrateSession(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  if (snapshot.v === 3) {
    return {
      ...snapshot,
      v: 4,
      windows: (snapshot.windows ?? []).map((win) => ({ ...win, props: win.props ?? {} })),
    };
  }
  return snapshot.v === SESSION_VERSION ? snapshot : null;
}

export function validateSession(snapshot) {
  if (!snapshot || snapshot.v !== SESSION_VERSION || !Array.isArray(snapshot.windows)) return null;
  const windows = snapshot.windows
    .filter((win) => win && Number.isFinite(win.id) && typeof win.appId === 'string')
    .map((win) => ({
      ...win,
      x: Number.isFinite(win.x) ? win.x : 120,
      y: Number.isFinite(win.y) ? win.y : 80,
      w: Number.isFinite(win.w) ? win.w : 440,
      h: Number.isFinite(win.h) ? win.h : null,
      props: win.props && typeof win.props === 'object' ? win.props : {},
      meta: win.meta && typeof win.meta === 'object' ? win.meta : {},
    }));
  return {
    ...snapshot,
    windows,
    spaces: Array.isArray(snapshot.spaces) && snapshot.spaces.length
      ? snapshot.spaces
      : [{ id: 1, name: 'Desktop 1' }],
    activeSpaceId: Number.isFinite(snapshot.activeSpaceId) ? snapshot.activeSpaceId : 1,
  };
}

/** Clear the saved session (used by "Reset Desktop" in System Preferences). */
export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

// ─── App-specific preferences ──────────────────────────────────────────────

const PREFS_KEY = 'aquaos_prefs_v1';

export function savePrefs(key, value) {
  try {
    const all = loadAllPrefs();
    all[key] = value;
    localStorage.setItem(PREFS_KEY, JSON.stringify(all));
  } catch (e) {
    console.warn('[persist] Failed to save pref:', key, e);
  }
}

export function loadPref(key, defaultValue = null) {
  const all = loadAllPrefs();
  return key in all ? all[key] : defaultValue;
}

function loadAllPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
